/**
 * Backtest: how often a rule would have held over the last ninety days, per
 * entity - answered by a watcher, never here.
 *
 * Explorer asks (`POST /api/argus/backtests`), a watcher claims the request and
 * runs its live `evaluate()` over history, and this panel shows the progress
 * and then the answer (`docs/plans/argus.md`, *Backtest*; migration 0029).
 * The same panel sits in the Rules room's drawer, for a saved rule, and in the
 * editor's Check panel, for the draft on screen - saved or not.
 *
 * If no watcher is alive it says so and offers nothing else: a backtest that
 * cannot run never estimates (rule 9).
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import {
  getArgusBacktest,
  getArgusRuleBacktest,
  requestArgusBacktest,
} from '../../api/endpoints.ts';
import { renderBacktestStrip } from './argus-backtest-strip.ts';
import { utcSeconds } from './argus-backtest-chart.ts';
import './argus-backtest-chart.ts';
import { StoreController } from '../../state/controller.ts';
import {
  backtestQueue,
  backtestSelected,
  queueWords,
  refreshBacktestQueue,
  waitingWords,
} from './argus-backtest-store.ts';
import { setArgusRoom } from './argus-lens.ts';
import { loadRules, rules, sources } from './argus-rules-store.ts';
import {
  DEFAULT_WINDOW,
  windowProblem,
  windowRequest,
  windowWords,
  type BacktestWindow,
} from './argus-window-pick.ts';
import './argus-window-pick.ts';
import type {
  ArgusBacktest,
  ArgusBacktestResult,
  ArgusRuleDocument,
  ArgusTreeDocument,
} from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';

const DAYS = 90;
const POLL_MS = 1500;

export function hoursWords(hours: number | null | undefined): string {
  if (hours == null) return '-';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${Math.round(hours * 10) / 10} h`;
  return `${Math.round(hours / 24)} days`;
}

export function plural(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

/** The one headline: "3 episodes on 1 of 11 ships over 90 days". */
export function backtestHeadline(result: ArgusBacktestResult): string {
  const t = result.totals;
  if (!t.episodes) {
    return `Nothing would have fired on any of the ${plural(t.entities, result.entity_noun)} over ${result.days} days.`;
  }
  const parts = [
    `${plural(t.episodes, 'episode')} on ${t.entities_firing} of ${plural(t.entities, result.entity_noun)} over ${result.days} days`,
  ];
  if (t.median_hours != null) parts.push(`median ${hoursWords(t.median_hours)}`);
  parts.push(`${hoursWords(t.hours)} in episodes`);
  return parts.join(' · ');
}

@customElement('argus-backtest-panel')
export class ArgusBacktestPanel extends LightElement {
  /** A saved rule. Without `document`, its current version is tested. */
  @property({ attribute: false }) ruleId: string | null = null;
  @property({ attribute: false }) ruleVersion: number | null = null;
  @property({ attribute: false }) ruleName = '';
  /** The draft on screen, in the editor. Tested as it is, saved or not. */
  @property({ attribute: false }) document: ArgusRuleDocument | ArgusTreeDocument | null = null;
  /** "ship", "meter" - the source's word, for the progress line. */
  @property({ attribute: false }) noun = 'entity';
  /** False while the draft is not a rule yet: there is nothing to test. */
  @property({ type: Boolean }) ready = true;
  /** The editor's panel is narrower and has its own heading style. */
  @property({ type: Boolean }) compact = false;
  /** The editor's full-width zone: headline numbers, every entity's row, and
   *  the chart around an episode picked on it. */
  @property({ type: Boolean }) wide = false;

  /** Seconds of history the rule's source keeps, or null for all of it (Live: 48 h). */
  private history(): number | null {
    // The drawer passes the rule only; the editor passes what is on screen.
    const key =
      this.document && 'source' in this.document
        ? this.document.source
        : rules.value.find((r) => r.id === this.ruleId)?.source_key;
    return sources.value.find((src) => src.key === key)?.history_seconds ?? null;
  }

  /** What a backtest covers, in words: "48 hours" on Live, "90 days" elsewhere. */
  private span(): string {
    const history = this.history();
    return history ? `${history / 3600} hours` : `${DAYS} days`;
  }

  @state() private run: ArgusBacktest | null = null;
  @state() private error: string | null = null;
  @state() private asking = false;
  /** The episode picked on the wide timeline, drawn on the chart under it. */
  @state() private picked: { entity: string; start: string } | null = null;
  /** The window, chosen in the editor's wide zone (roadmap B1); the drawer runs 90 days. */
  @state() private window: BacktestWindow = DEFAULT_WINDOW;
  private lastSummary = '';
  /** The draft as it was when this run was asked for, to say when it has changed. */
  private askedFor: string | null = null;
  private timer: number | undefined;
  private loadedFor: string | null = null;
  // `rules`: a chart chosen in the editor shows here at once (T3).
  private store = new StoreController(this, [backtestQueue, rules]);

  override connectedCallback(): void {
    super.connectedCallback();
    void refreshBacktestQueue();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    window.clearTimeout(this.timer);
  }

  override updated(): void {
    this.announce();
    if (this.document && this.ruleId && this.wide) {
      this.loadEditorRun();
      return;
    }
    // In the drawer, show the newest run of the rule's current version.
    if (this.document || !this.ruleId) return;
    const key = `${this.ruleId}@${this.ruleVersion}`;
    if (key === this.loadedFor) return;
    this.loadedFor = key;
    this.run = null;
    this.error = null;
    window.clearTimeout(this.timer);
    void getArgusRuleBacktest(this.ruleId)
      .then((answer) => {
        this.run = answer.backtest;
        this.follow();
      })
      .catch(() => {
        // No earlier run to show is the ordinary case; say nothing.
      });
  }

  /**
   * In the editor, the rule's newest run, so the zone opens on what is known
   * rather than empty. It was a run of the saved version: once the draft on
   * screen differs from it - or it was of an older version - it says so.
   */
  private loadEditorRun(): void {
    const key = `${this.ruleId}@editor`;
    if (key === this.loadedFor) return;
    this.loadedFor = key;
    const openedWith = JSON.stringify(this.document);
    void getArgusRuleBacktest(this.ruleId!)
      .then((answer) => {
        if (this.run) return; // one asked for meanwhile wins
        this.run = answer.backtest;
        this.askedFor = answer.backtest?.rule_version === this.ruleVersion ? openedWith : 'another version';
        this.follow();
      })
      .catch(() => {
        // No earlier run is the ordinary case; say nothing.
      });
  }

  private stale(): boolean {
    return !!this.document && !!this.askedFor && this.askedFor !== JSON.stringify(this.document);
  }

  /** One line for a folded zone's heading: `argus-backtest-summary`. */
  private announce(): void {
    const run = this.run;
    const busy = this.asking || run?.status === 'queued' || run?.status === 'running';
    let text = 'Not run yet';
    let tone = 'none';
    if (busy) {
      text = run?.total_entities ? `Running - ${run.done_entities} of ${plural(run.total_entities, this.noun)}` : 'Running';
      tone = 'busy';
    } else if (run?.status === 'failed') {
      text = 'Could not run';
      tone = 'error';
    } else if (run?.status === 'done' && run.result) {
      const t = run.result.totals;
      text = t.episodes
        ? `${plural(t.episodes, 'episode')} on ${t.entities_firing} of ${plural(t.entities, run.result.entity_noun)}, ${run.result.days} days`
        : `Nothing would have fired in ${run.result.days} days`;
      tone = 'done';
      if (this.stale()) {
        text += ' · older than your change';
        tone = 'stale';
      }
    }
    const key = `${tone}|${text}`;
    if (key === this.lastSummary) return;
    this.lastSummary = key;
    this.dispatchEvent(new CustomEvent('argus-backtest-summary', { detail: { text, tone } }));
  }

  private follow(): void {
    window.clearTimeout(this.timer);
    const run = this.run;
    if (!run || run.status === 'done' || run.status === 'failed') {
      if (run) void refreshBacktestQueue();
      return;
    }
    this.timer = window.setTimeout(async () => {
      try {
        this.run = await getArgusBacktest(run.id);
        // A finished run of a saved rule's version is what deploying it waits
        // for (migration 0033): read the rules again so the switch knows.
        if (this.run.status === 'done' && this.run.rule_id) void loadRules();
      } catch {
        // A missed poll is retried; the watcher's answer is in the database.
      }
      this.follow();
    }, POLL_MS);
  }

  private async ask(): Promise<void> {
    this.asking = true;
    this.error = null;
    try {
      this.askedFor = this.document ? JSON.stringify(this.document) : null;
      this.picked = null;
      this.run = await requestArgusBacktest({
        rule_id: this.ruleId,
        document: this.document,
        ...(this.wide ? windowRequest(this.window) : { days: DAYS }),
        label: this.ruleName,
      });
      this.follow();
      void refreshBacktestQueue();
    } catch (error) {
      this.error = userMessage(error, 'The backtest could not be asked for.');
    } finally {
      this.asking = false;
    }
  }

  private renderProgress(run: ArgusBacktest): TemplateResult {
    const total = run.total_entities ?? 0;
    const share = total ? Math.round((100 * run.done_entities) / total) : 0;
    const words =
      run.status === 'queued'
        ? waitingWords(run.ahead)
        : total
          ? `${run.done_entities} of ${plural(total, this.noun)}`
          : 'Starting';
    return html`<div class="argus-bt-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow=${share}>
      <div class="progress-bar-track">
        <div class="progress-bar-fill ${run.status === 'queued' ? 'is-waiting' : ''}" style=${`width:${run.status === 'queued' ? 100 : share}%`}></div>
      </div>
      <span class="progress-text">${words}</span>
    </div>`;
  }

  /** Before asking: say the watchers are busy, so a wait is not a surprise. */
  private renderQueue(): TemplateResult | typeof nothing {
    const words = queueWords(backtestQueue.value);
    return words ? html`<p class="argus-lfaint argus-bt-queue" role="status">${words}</p>` : nothing;
  }

  private renderResult(run: ArgusBacktest, result: ArgusBacktestResult): TemplateResult {
    const stale = this.document && this.askedFor && this.askedFor !== JSON.stringify(this.document);
    return html`
      <p class="argus-bt-headline">${backtestHeadline(result)}</p>
      ${stale ? html`<p class="argus-bt-note is-stale">The rule has changed since this ran.</p>` : nothing}
      ${result.totals.episodes ? renderBacktestStrip(result, { maxRows: 6 }) : nothing}
      <div class="argus-bt-foot">
        <span class="argus-lfaint">
          ${new Date(run.finished_at ?? run.requested_at).toLocaleString(undefined, {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
          · ${run.requested_by_name} · ${Math.round(result.seconds)} s
        </span>
        <button type="button" class="argus-lmore" @click=${() => this.openInRoom(run.id)}>Open in Backtest →</button>
      </div>
    `;
  }

  private renderWide(run: ArgusBacktest | null, busy: boolean): TemplateResult {
    const result = run?.status === 'done' ? run.result : null;
    const t = result?.totals;
    const noun = result?.entity_noun ?? this.noun;
    return html`
      <section class="argus-bt is-wide">
        <div class="argus-bt-wide-top">
          ${t
            ? html`<dl class="argus-bt-stats">
                <div><dt>Episodes</dt><dd>${t.episodes.toLocaleString()}</dd></div>
                <div>
                  <dt>${noun}s firing</dt>
                  <dd>${t.entities_firing}<small>of ${t.entities}</small></dd>
                </div>
                <div>
                  <dt>Time in episodes</dt>
                  <dd>${hoursWords(t.hours)}</dd>
                  <small>all ${noun}s together${t.median_hours != null ? html` · median ${hoursWords(t.median_hours)}` : nothing}</small>
                </div>
                <div><dt>Still open</dt><dd>${t.still_open}</dd><small>at the end of the range</small></div>
              </dl>`
            : html`<p class="argus-lfaint">
                How often this rule would have fired over the window, per ${this.noun}, run by the
                watcher over real history.
              </p>`}
          <div class="argus-bt-wide-run">
            ${this.history()
              ? html`<span class="argus-lfaint" title="The server backtests a source that keeps a short history over all of it.">
                  Last ${this.span()}: all the data this source keeps
                </span>`
              : html`<argus-window-pick
                  .value=${this.window}
                  .noun=${this.noun}
                  @window-change=${(e: CustomEvent<BacktestWindow>) => (this.window = e.detail)}
                ></argus-window-pick>`}
            <button
              type="button"
              class="dash-btn argus-bt-run"
              ?disabled=${this.asking || !this.ready || (!this.history() && !!windowProblem(this.window))}
              @pointerenter=${() => void refreshBacktestQueue()}
              @focus=${() => void refreshBacktestQueue()}
              @click=${() => void this.ask()}
            >
              ${run && !busy ? 'Run again' : this.history() ? `Backtest ${this.span()}` : 'Backtest'}
            </button>
          </div>
        </div>
        ${run?.result ? html`<p class="argus-lfaint">This run: ${windowWords(run)}.</p>` : nothing}
        ${this.renderQueue()}
        ${this.error ? html`<p class="argus-bt-note is-error" role="alert">${this.error}</p>` : nothing}
        ${run && busy ? this.renderProgress(run) : nothing}
        ${run?.status === 'failed' ? html`<p class="argus-bt-note is-error">Could not run: ${run.error}</p>` : nothing}
        ${result && this.stale()
          ? html`<div class="argus-bt-stale">
              <span>
                ${this.askedFor === 'another version'
                  ? 'This ran on an earlier version of the rule.'
                  : 'The rule has changed since this ran.'}
                The timeline is the rule as it was then.
              </span>
              <button type="button" class="dash-btn" ?disabled=${this.asking || !this.ready} @click=${() => void this.ask()}>
                Run with the change
              </button>
            </div>`
          : nothing}
        ${result && run
          ? html`
              ${result.entities.length
                ? renderBacktestStrip(result, {
                    wide: true,
                    picked: this.picked,
                    onPick: (entity, start) => this.pick(entity, start),
                  })
                : nothing}
              ${this.renderPeek(run, result)}
              <div class="argus-bt-foot">
                <span class="argus-lfaint">
                  ${new Date(run.finished_at ?? run.requested_at).toLocaleString(undefined, {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  · ${run.requested_by_name} · ${Math.round(result.seconds)} s
                </span>
                <button type="button" class="argus-lmore" @click=${() => this.openInRoom(run.id)}>Open in Backtest →</button>
              </div>
            `
          : nothing}
      </section>
    `;
  }

  /**
   * Pick an episode. The rule's newest run comes without what to chart (the
   * drawer never draws it), so the first pick asks for the whole run.
   */
  private pick(entity: string, start: string): void {
    this.picked = { entity, start };
    const run = this.run;
    if (!run || run.chart !== undefined) return;
    void getArgusBacktest(run.id)
      .then((full) => {
        if (this.run?.id === run.id) this.run = { ...this.run, chart: full.chart ?? null };
      })
      .catch(() => {
        if (this.run?.id === run.id) this.run = { ...this.run, chart: null };
      });
  }

  /** The series around the picked episode, on Explore's chart, the episode shaded. */
  private renderPeek(run: ArgusBacktest, result: ArgusBacktestResult): TemplateResult | typeof nothing {
    if (!result.totals.episodes) return nothing;
    const picked = this.picked;
    const episode = picked
      ? result.episodes.find((e) => e.entity === picked.entity && e.start === picked.start)
      : undefined;
    if (!picked || !episode) {
      return html`<p class="argus-lfaint">Click an episode on the timeline to see the signal around it.</p>`;
    }
    const start = utcSeconds(episode.start);
    const end = utcSeconds(episode.end ?? episode.last);
    const pad = Math.max(end - start, 6 * 3600);
    const lo = Math.max(utcSeconds(result.range_from), start - pad);
    const hi = Math.min(utcSeconds(result.range_to), end + pad);
    const naive = (sec: number): string => new Date(sec * 1000).toISOString().slice(0, 19);
    const name = result.entities.find((e) => e.entity === picked.entity)?.name ?? picked.entity;
    return html`<div class="argus-bt-peek">
      <div class="argus-bt-peek-head">
        <b>${name}</b>
        <span class="argus-lfaint">
          ${new Date(start * 1000).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          · ${hoursWords((end - start) / 3600)}${episode.end ? '' : ', still holding at the end'}
        </span>
        <button type="button" class="argus-lmore" @click=${() => (this.picked = null)}>Close</button>
      </div>
      ${run.chart === undefined
        ? html`<p class="argus-lfaint">Reading the chart…</p>`
        : run.chart
        ? html`<argus-backtest-chart
            .spec=${run.chart[picked.entity] ?? null}
            .episodes=${result.episodes.filter((e) => e.entity === picked.entity)}
            .entity=${picked.entity}
            .from=${naive(lo)}
            .to=${naive(hi)}
            .source=${run.source_key}
            .ruleId=${this.ruleId ?? ''}
            .charts=${rules.value.find((r) => r.id === this.ruleId)?.charts ?? []}
          ></argus-backtest-chart>`
        : html`<p class="argus-lfaint">This run's rule cannot be read back, so there is nothing to draw.</p>`}
    </div>`;
  }

  private openInRoom(id: string): void {
    backtestSelected.set(id);
    setArgusRoom('backtest');
  }

  override render(): TemplateResult {
    const run = this.run;
    const busy = this.asking || run?.status === 'queued' || run?.status === 'running';
    if (this.wide) return this.renderWide(run, busy);
    return html`
      <section class="argus-bt ${this.compact ? 'is-compact' : ''}">
        <div class="argus-bt-head">
          ${this.compact
            ? html`<span class="argus-lfaint">Last ${this.span()}</span>`
            : html`<h4>Backtest <span class="argus-lfaint">last ${this.span()}</span></h4>`}
          <button
            type="button"
            class="dash-btn argus-bt-run"
            ?disabled=${this.asking || !this.ready}
            @pointerenter=${() => void refreshBacktestQueue()}
            @focus=${() => void refreshBacktestQueue()}
            @click=${() => void this.ask()}
          >
            ${run && !busy ? 'Run again' : `Backtest ${this.span()}`}
          </button>
        </div>
        ${this.renderQueue()}
        ${this.error ? html`<p class="argus-bt-note is-error" role="alert">${this.error}</p>` : nothing}
        ${!run && !this.error
          ? html`<p class="argus-lfaint">
              How often this rule would have fired, per ${this.noun}, run by the watcher over real history.
            </p>`
          : nothing}
        ${run && busy ? this.renderProgress(run) : nothing}
        ${run?.status === 'failed'
          ? html`<p class="argus-bt-note is-error">Could not run: ${run.error}</p>`
          : nothing}
        ${run?.status === 'done' && run.result ? this.renderResult(run, run.result) : nothing}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-backtest-panel': ArgusBacktestPanel;
  }
}
